import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Image as ImageIcon,
  Loader2,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  BUSINESS_PHOTO_CATEGORIES,
  BusinessPhotoCategory,
  BusinessPhotoItem,
  importBusinessPhotosToMediaLibrary,
  listBusinessPhotosByCategory,
} from '../lib/businessPhotoCatalog';
import { mediaThumbUrl, type MediaItem } from '../lib/mediaLibrary';

interface ChooseFromImagesModalProps {
  isOpen: boolean;
  businessId: string;
  onClose: () => void;
  /** Called after selected photos are added to media_library. */
  onImported: (created: MediaItem[]) => void;
}

const PAGE_SIZE = 60;

export default function ChooseFromImagesModal({
  isOpen,
  businessId,
  onClose,
  onImported,
}: ChooseFromImagesModalProps) {
  const [category, setCategory] = useState<BusinessPhotoCategory | null>(null);
  const [photos, setPhotos] = useState<BusinessPhotoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCategory(null);
    setPhotos([]);
    setSelectedKeys(new Set());
    setHasMore(false);
  }, [isOpen, businessId]);

  useEffect(() => {
    if (!isOpen || !category || !businessId) return;
    let cancelled = false;
    setLoading(true);
    setPhotos([]);
    setSelectedKeys(new Set());
    setHasMore(false);
    listBusinessPhotosByCategory(businessId, category, { limit: PAGE_SIZE, offset: 0 })
      .then((items) => {
        if (cancelled) return;
        setPhotos(items);
        setHasMore(items.length >= PAGE_SIZE);
      })
      .catch((err) => {
        console.error(err);
        toast.error(err instanceof Error ? err.message : 'Failed to load images');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, category, businessId]);

  if (!isOpen) return null;

  const toggle = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const loadMore = async () => {
    if (!category || loadingMore) return;
    setLoadingMore(true);
    try {
      const more = await listBusinessPhotosByCategory(businessId, category, {
        limit: PAGE_SIZE,
        offset: photos.length,
      });
      setPhotos((prev) => {
        const seen = new Set(prev.map((p) => p.key));
        return [...prev, ...more.filter((p) => !seen.has(p.key))];
      });
      setHasMore(more.length >= PAGE_SIZE);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load more images');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleImport = async () => {
    const selected = photos.filter((p) => selectedKeys.has(p.key));
    if (selected.length === 0) {
      toast.error('Select at least one image');
      return;
    }
    setImporting(true);
    try {
      const { created, skipped } = await importBusinessPhotosToMediaLibrary(
        businessId,
        selected,
      );
      if (created.length > 0) {
        toast.success(
          created.length === 1
            ? 'Image added to media library'
            : `${created.length} images added to media library`,
        );
        onImported(created);
      } else if (skipped > 0) {
        toast.success(
          skipped === 1
            ? 'That image is already in your media library'
            : 'Those images are already in your media library',
        );
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to add images');
    } finally {
      setImporting(false);
    }
  };

  const categoryMeta = category
    ? BUSINESS_PHOTO_CATEGORIES.find((c) => c.id === category)
    : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100300] bg-black/50 flex items-center justify-center p-0 sm:p-6"
      style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:max-h-[86vh] sm:max-w-3xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        // Full-screen on mobile — keep header/footer clear of the iOS notch & home indicator
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            {category && (
              <button
                type="button"
                onClick={() => setCategory(null)}
                className="h-8 w-8 flex items-center justify-center text-gray-500 hover:text-gray-800 rounded-full hover:bg-gray-100"
                aria-label="Back"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 truncate">
                {categoryMeta ? categoryMeta.label : 'Choose from images'}
              </h2>
              <p className="text-[12px] text-gray-500 truncate">
                {categoryMeta
                  ? 'Select images to add to your media library'
                  : 'Pick a source, then choose images'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!category ? (
            <div className="space-y-2">
              {BUSINESS_PHOTO_CATEGORIES.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setCategory(opt.id)}
                  className="w-full flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-left hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  <span className="h-10 w-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-gray-900">{opt.label}</span>
                    <span className="block text-[12px] text-gray-500 mt-0.5">{opt.description}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : loading ? (
            <div className="flex justify-center py-16 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : photos.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-14 text-center">
              <ImageIcon className="h-7 w-7 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No images found in this category</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map((photo) => {
                  const selected = selectedKeys.has(photo.key);
                  return (
                    <button
                      key={photo.key}
                      type="button"
                      onClick={() => toggle(photo.key)}
                      className={`group relative aspect-square rounded-xl overflow-hidden border-2 bg-gray-50 transition-colors ${
                        selected ? 'border-blue-600' : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={mediaThumbUrl(photo.url)}
                        alt={photo.fileName || 'Photo'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <span
                        className={`absolute top-2 right-2 h-6 w-6 rounded-full flex items-center justify-center shadow-sm ${
                          selected
                            ? 'bg-blue-600 text-white'
                            : 'bg-white/90 text-transparent border border-gray-200'
                        }`}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      {photo.fileName && (
                        <span className="absolute inset-x-0 bottom-0 bg-black/45 text-white text-[10px] px-1.5 py-1 truncate">
                          {photo.fileName}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={() => void loadMore()}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {category && (
          <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
            <p className="text-[12px] text-gray-500">
              {selectedKeys.size === 0
                ? 'No images selected'
                : `${selectedKeys.size} selected`}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleImport()}
                disabled={importing || selectedKeys.size === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Add to media library
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
